import React, { useState, useCallback } from 'react';
import {
  FiZap,
  FiAlertTriangle,
  FiActivity,
  FiExternalLink,
  FiCheckCircle,
  FiLoader,
  FiCalendar,
  FiUser,
  FiClock,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';
import { apiService } from '../../service/Api.js';

// ─── Tier Config ──────────────────────────────────────────────────────────────

const TIER_CONFIG = {
  T1: {
    label: 'T1 — Act within 48 h',
    dotColor: 'bg-red-500',
    badgeBg: 'bg-red-50',
    badgeBorder: 'border-red-200',
    badgeText: 'text-red-700',
    headerBg: 'bg-red-50',
    icon: FiZap,
    iconColor: 'text-red-500',
  },
  T2: {
    label: 'T2 — Act within 7 days',
    dotColor: 'bg-amber-500',
    badgeBg: 'bg-amber-50',
    badgeBorder: 'border-amber-200',
    badgeText: 'text-amber-700',
    headerBg: 'bg-amber-50',
    icon: FiAlertTriangle,
    iconColor: 'text-amber-500',
  },
  T3: {
    label: 'T3 — Monitor',
    dotColor: 'bg-blue-400',
    badgeBg: 'bg-blue-50',
    badgeBorder: 'border-blue-200',
    badgeText: 'text-blue-700',
    headerBg: 'bg-blue-50',
    icon: FiActivity,
    iconColor: 'text-blue-500',
  },
};

const SOURCE_TYPE_LABELS = {
  bse_filing: 'BSE Filing',
  linkedin: 'LinkedIn',
  news: 'News',
  press_release: 'Press Release',
  mca: 'MCA Filing',
  rbi_gazette: 'RBI Gazette',
  other: 'Other',
};

// ─── Signal Card ──────────────────────────────────────────────────────────────

const SignalCard = ({ signal, companySourceId, tier }) => {
  const [expanded, setExpanded] = useState(false);
  const [taskStatus, setTaskStatus] = useState('idle'); // idle | loading | done | error
  const [taskError, setTaskError] = useState(null);

  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.T3;

  const handleCreateTask = useCallback(async () => {
    setTaskStatus('loading');
    setTaskError(null);
    try {
      await apiService.createSignalTask({
        companySourceId,
        taskSubject: signal.headline,
        taskBody: `[${signal.signal_type}] ${signal.recommended_action}\n\nEvidence: ${signal.evidence}\nSource: ${signal.source_url || 'N/A'}`,
      });
      setTaskStatus('done');
    } catch (err) {
      setTaskStatus('error');
      setTaskError(err.response?.data?.message || err.message || 'Failed to create task');
    }
  }, [signal, companySourceId]);

  const sourceLabel = SOURCE_TYPE_LABELS[signal.source_type] || signal.source_type || 'Source';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-3 transition-shadow hover:shadow-md">
      {/* Card Header */}
      <div
        className={`${cfg.headerBg} px-4 py-3 flex items-start gap-3 cursor-pointer select-none`}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className={`mt-0.5 flex-shrink-0 ${cfg.iconColor}`}>
          {React.createElement(cfg.icon, { className: 'w-4 h-4' })}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.badgeBg} ${cfg.badgeBorder} ${cfg.badgeText}`}>
              {tier}
            </span>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {signal.signal_type?.replace(/_/g, ' ')}
            </span>
            {signal.suggested_contact_angle && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <FiUser className="w-3 h-3" />
                {signal.suggested_contact_angle}
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-gray-800 leading-snug">
            {signal.headline}
          </p>
        </div>
        <div className="flex-shrink-0 mt-0.5 text-gray-400">
          {expanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded Body */}
      {expanded && (
        <div className="px-4 py-4 space-y-3">
          {/* Evidence */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence</p>
            <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-100 italic">
              "{signal.evidence}"
            </p>
          </div>

          {/* Source + Date row */}
          <div className="flex items-center gap-4 flex-wrap">
            {signal.source_url && (
              <a
                href={signal.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
              >
                <FiExternalLink className="w-3 h-3" />
                {sourceLabel}
              </a>
            )}
            {signal.detected_date && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiCalendar className="w-3 h-3" />
                {new Date(signal.detected_date).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
              </span>
            )}
            {signal.sla_hours && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiClock className="w-3 h-3" />
                {signal.sla_hours < 48 ? `${signal.sla_hours}h SLA` : `${signal.sla_hours / 24}d SLA`}
              </span>
            )}
          </div>

          {/* Recommended Action */}
          {signal.recommended_action && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recommended Action</p>
              <p className="text-sm text-gray-800 leading-relaxed">{signal.recommended_action}</p>
            </div>
          )}

          {/* CTA */}
          <div className="pt-1 flex items-center gap-3">
            {taskStatus === 'idle' && (
              <button
                onClick={handleCreateTask}
                className="flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors duration-150"
              >
                <FiCheckCircle className="w-3.5 h-3.5" />
                Add as HubSpot Task
              </button>
            )}
            {taskStatus === 'loading' && (
              <span className="flex items-center gap-2 text-xs text-gray-500">
                <FiLoader className="w-3.5 h-3.5 animate-spin" />
                Creating task…
              </span>
            )}
            {taskStatus === 'done' && (
              <span className="flex items-center gap-2 text-xs text-green-600 font-medium">
                <FiCheckCircle className="w-3.5 h-3.5" />
                Task created in HubSpot
              </span>
            )}
            {taskStatus === 'error' && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-red-600">{taskError}</span>
                <button
                  onClick={handleCreateTask}
                  className="text-xs text-gray-500 underline hover:text-gray-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tier Group ───────────────────────────────────────────────────────────────

const TierGroup = ({ tier, signals, companySourceId }) => {
  const [open, setOpen] = useState(tier !== 'T3');
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.T3;

  return (
    <div className="mb-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 mb-3 group"
      >
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dotColor}`} />
        <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">
          {cfg.label}
        </span>
        <span className="ml-1 text-xs font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
          {signals.length}
        </span>
        <span className="ml-auto text-gray-400 group-hover:text-gray-600">
          {open ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
        </span>
      </button>

      {open && signals.map((signal, idx) => (
        <SignalCard
          key={idx}
          signal={signal}
          tier={tier}
          companySourceId={companySourceId}
        />
      ))}
    </div>
  );
};

// ─── Scan Meta Bar ────────────────────────────────────────────────────────────

const ScanMeta = ({ scanRecord }) => {
  const signal = scanRecord.signal || {};
  const scanDate = signal.scan_date
    ? new Date(signal.scan_date).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="flex items-center gap-3 text-xs text-gray-400 mb-4 pl-1">
      {scanDate && (
        <span className="flex items-center gap-1">
          <FiCalendar className="w-3 h-3" />
          Scanned {scanDate}
        </span>
      )}
      {signal.actors_used?.length > 0 && (
        <span>{signal.actors_used.length} Apify actors used</span>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SignalsTab = ({ signalsData, signalsLoading, signalsError, companySourceId, Section }) => {
  if (signalsLoading) {
    return (
      <Section title="Account Signals" iconComponent={FiZap}>
        <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
          <FiLoader className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading signals…</span>
        </div>
      </Section>
    );
  }

  if (signalsError) {
    return (
      <Section title="Account Signals" iconComponent={FiZap}>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {signalsError}
        </div>
      </Section>
    );
  }

  if (!signalsData || signalsData.length === 0) {
    return (
      <Section title="Account Signals" iconComponent={FiZap}>
        <div className="text-center py-12 text-gray-400 text-sm">
          No signals found for this company yet.
        </div>
      </Section>
    );
  }

  // Flatten all signals across all scan runs, attaching tier from each signal item
  const allSignals = [];
  signalsData.forEach((record) => {
    const signals = record.signal?.signals || [];
    signals.forEach((s) => allSignals.push(s));
  });

  const t1 = allSignals.filter((s) => s.tier === 'T1');
  const t2 = allSignals.filter((s) => s.tier === 'T2');
  const t3 = allSignals.filter((s) => s.tier === 'T3');

  const latestScan = signalsData[0]; // newest first (ordered by created_at DESC)

  return (
    <Section title="Account Signals" iconComponent={FiZap}>
      <ScanMeta scanRecord={latestScan} />

      {/* Summary pills */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[
          { tier: 'T1', count: t1.length, bg: 'bg-red-100', text: 'text-red-700' },
          { tier: 'T2', count: t2.length, bg: 'bg-amber-100', text: 'text-amber-700' },
          { tier: 'T3', count: t3.length, bg: 'bg-blue-100', text: 'text-blue-700' },
        ].map(({ tier, count, bg, text }) => count > 0 && (
          <span key={tier} className={`${bg} ${text} text-xs font-semibold px-3 py-1 rounded-full`}>
            {count} {tier}
          </span>
        ))}
        <span className="text-xs text-gray-400 ml-1">{allSignals.length} total</span>
      </div>

      {/* Tier groups */}
      {t1.length > 0 && <TierGroup tier="T1" signals={t1} companySourceId={companySourceId} />}
      {t2.length > 0 && <TierGroup tier="T2" signals={t2} companySourceId={companySourceId} />}
      {t3.length > 0 && <TierGroup tier="T3" signals={t3} companySourceId={companySourceId} />}

      {allSignals.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          Scans found, but no signals detected for this account.
        </div>
      )}
    </Section>
  );
};

export default SignalsTab;
